<template>
  <div class="content-wrapper">
    <app-breadcrumb
      :page-title="$t('tasks')"
      :directory="$t('tasks')"
      :icon="'sidebar'"
    />
    <div class="card card-with-shadow border-0">
      <div class="card-header p-primary bg-transparent">
        <h5 class="card-title m-0">{{ title }}</h5>
      </div>
      <app-overlay-loader v-if="preloader" />
      <div class="card-body">
        <el-form v-if="active === 1">

          <form
              ref="form"
              data-url="/admin/task/store"
              class="mb-sm-0"
              id="task_form"
              :class="{ 'loading-opacity': preloader }"
          >
            <el-collapse v-model="activeAccordion">
              <!-- ============================== -->
              <!-- Accordion 1: Core / Required  -->
              <!-- ============================== -->
              <el-collapse-item name="core" title="Task, Users & Forms">
                <!-- Title + Users -->
                <div class="row">
                  <!-- Task Title (required) -->
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="form-group">
                      <label>{{ $t("title") }}</label>
                      <app-input
                          type="text"
                          v-model="inputs.title"
                          :required="true"
                      />
                    </div>
                  </div>

                  <!-- Users (required) -->
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="form-group">
                      <label>{{ $t("users") }}</label>
                      <app-input
                          type="multi-select"
                          :placeholder="$t('select_user')"
                          :list="this.users"
                          v-model="inputs.select_users"
                          :required="true"
                      />
                      <p class="font-italic text-muted">
                        {{ $t("shortcut_user_create") }}
                        <span>
                <a href="#" @click.prevent="addUser()">
                  {{ $t("add_user") }}
                </a>
              </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Forms + Publish + Preview  AND  Task Category -->
                <div class="row">
                  <!-- Forms + Publish + Preview (required forms) -->
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="form-group">
                      <label for="inputs_forms">{{ $t("forms") }}</label>
                      <app-input
                          type="multi-select"
                          :isAnimatedDropdown="true"
                          :placeholder="$t('forms')"
                          :list="forms"
                          v-model="inputs.form_id"
                          :required="true"
                      />

                      <p v-if="refreshShow === false" class="font-italic text-muted">
                        {{ $t("shortcut_form_create") }}
                        <span>
                <a href="#" @click.prevent="createForm()">
                  {{ $t("create_new") }}
                </a>
              </span>
                      </p>
                      <p v-if="refreshShow === true" class="font-italic text-muted">
                        <a href="#" @click.prevent="refreshForm()">
                          {{ $t("refresh") }}
                        </a>
                        <span> {{ $t("form_create_refresh") }}</span>
                      </p>

                      <!-- Publish Check -->
                      <el-switch
                          v-model="inputs.form_published"
                          active-text="Publish Forms Now"
                          inactive-text="Publish Forms Later"
                          :active-value="true"
                          :inactive-value="false"
                      />

                      <!-- Forms Preview (kept together with forms selection) -->
                      <div class="mt-3">
                        <p v-if="inputs.form_id && inputs.form_id.length > 0">
                          {{ $t('preview_selected_forms') || 'Click to preview selected forms:' }}
                        </p>
                        <div
                            v-for="form_id in inputs.form_id"
                            :key="form_id"
                        >
                          <a :href="'/form/preview/' + form_id" target="_blank">
                            {{ formName(form_id) }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Task Category (required) -->
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="form-group">
                      <label>{{ $t("task_category") }}</label>
                      <app-input
                          type="search-select"
                          :placeholder="$t('select_task_category')"
                          :list="this.taskCategories"
                          v-model="inputs.select_task_category"
                          :required="true"
                      />
                      <p
                          v-if="categoryAddShow == false"
                          class="font-italic text-muted"
                      >
                        {{ $t("shortcut_category_create") }}
                        <span>
                <a href="#" @click.prevent="addCategory()">
                  {{ $t("add_category") }}
                </a>
              </span>
                      </p>
                    </div>

                    <div v-if="categoryAddShow == true">
                      <div class="d-flex align-items-center">
                        <app-input
                            class="mr-2 flex-grow-1"
                            type="text"
                            placeholder="Enter Category Name"
                            v-model="category_name"
                        />
                        <button
                            type="submit"
                            class="btn btn-sm btn-primary mr-2"
                            @click.prevent="createCategory()"
                        >
                          {{ $t('save') }}
                        </button>
                        <a
                            href="#"
                            class="text-muted"
                            @click.prevent="closeCategory()"
                        >
                          <app-icon name="x" />
                        </a>
                      </div>
                      <small class="text-danger ml-2" v-html="message"></small>
                    </div>
                  </div>
                </div>

                <!-- Date / Time (required) -->
                <div
                    v-for="(date_time, k) in date_time_data"
                    :key="k"
                >
                  <div class="row">
                    <div class="col-lg-3 col-md-3 col-sm-12">
                      <div class="form-group">
                        <label>{{ $t("start_date") }}</label>
                        <app-input
                            type="date"
                            :required="true"
                            v-model="date_time.start_date"
                            :placeholder="$t('select_valid_date')"
                        />
                      </div>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                      <div class="form-group">
                        <label>{{ $t("start_time") }}</label>
                        <app-input
                            type="time"
                            :required="true"
                            :startOfTime="true"
                            v-model="date_time.start_time"
                            :placeholder="$t('select_valid_time')"
                        />
                      </div>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                      <div class="form-group">
                        <label>{{ $t("end_date") }}</label>
                        <app-input
                            type="date"
                            v-model="date_time.end_date"
                            :required="true"
                            :placeholder="$t('select_valid_date')"
                        />
                      </div>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                      <div class="form-group">
                        <label>{{ $t("end_time") }}</label>
                        <app-input
                            type="time"
                            :required="true"
                            :endOfTime="true"
                            v-model="date_time.end_time"
                            :placeholder="$t('select_valid_time')"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end">
                    <i
                        class="fas fa-minus-circle fa-2x mr-2"
                        @click.prevent="remove(k)"
                        v-show="k || (!k && date_time_data.length > 1)"
                    ></i>
                    <i
                        class="fas fa-plus-circle fa-2x"
                        @click.prevent="add(k)"
                        v-show="k == date_time_data.length - 1"
                    ></i>
                  </div>
                </div>
              </el-collapse-item>

              <!-- =============================== -->
              <!-- Accordion 2: Non-mandatory     -->
              <!-- =============================== -->
              <el-collapse-item name="extra" title="Other Details">
                <!-- Description + Customer -->
                <div class="row">
                  <!-- Description (optional) -->
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="form-group">
                      <label>{{ $t("description") }}</label>
                      <app-input
                          type="textarea"
                          id="textarea-id"
                          :label="$t('message_placeholder')"
                          v-model="inputs.description"
                      />
                    </div>
                  </div>

                  <!-- Customer (optional) -->
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="form-group">
                      <label>{{ $t("customer") }}</label>
                      <app-input
                          type="search-select"
                          :placeholder="$t('select_customer')"
                          :list="this.customers"
                          v-model="inputs.select_customer"
                      />
                    </div>
                  </div>
                </div>

                <!-- Priority (optional) -->
                <div class="row">
                  <div class="col-lg-12 col-md-12 col-sm-12">
                    <div class="form-group">
                      <label>{{ $t("priority") }}</label>
                      <app-input
                          type="radio"
                          :list="list"
                          v-model="inputs.priority"
                      />
                    </div>
                  </div>
                </div>

                <!-- Attachments (optional) -->
                <div class="row">
                  <div class="col-lg-12 col-md-12 col-sm-12">
                    <div class="form-group">
                      <app-input
                          type="dropzone"
                          v-model="dropzone_files"
                      />
                      <small class="text-muted font-italic">
                        {{ $t("recommended_dropzone_file_validation") }}
                      </small>
                    </div>
                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>

            <!-- Actions -->
            <div class="mt-5 action-buttons">
              <button
                  type="submit"
                  class="btn btn-primary mr-2"
                  @click.prevent="submitData"
              >
                {{ $t("submit") }}
              </button>
              <button
                  type="submit"
                  class="btn btn-secondary"
                  @click.prevent="clear"
              >
                {{ $t("clear") }}
              </button>
            </div>
          </form>


        </el-form>
       <el-form v-if="active === 2">
          <shortcut-user-create :back="backTaskCreate"></shortcut-user-create>
       </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import { FormMixin } from "../../../../../core/mixins/form/FormMixin";
import {
  GET_COMPANY_USERS,
  GET_PENDDING_TASKS,
  GET_TASK_CATEGORIES,
  CUSTOMERS_LIST,
  BUYER_CERTIFICATE_FORMS,
  TASK_CATEGORY_STORE,
  TASK_STORE,
  ROLES,
} from "../../../../Config/ApiUrl";
import AppLibrary from "../../../../Helpers/AppLibrary";
import { axiosGet, axiosPost } from "../../../../Helpers/AxiosHelper";

export default {
  name: "TaskCreate",
  extends: AppLibrary,
  mixins: [FormMixin],
  props:{
    requestData:{}
  },
  data() {
    return {
      activeAccordion: 'core',
      refreshShow: false,
      title: this.$t("store_tasks"),
      active: 1,
      cat: {},
      message: '',
      categoryAddShow: false,
      category_name: '',
      forms: [],
      checked_task_dependent: false,
      checked_repeat: false,
      preloader: false,
      // total_recuring_task: true,
      inputs: {
        priority: "High",
        select_users: [],
        form_id: [],
      },
      date_time_data:[{
        start_date: new Date(),
        end_date: new Date(),
        start_time: null,
        end_time: null
      }],
      dropzone_files: [],
      list: [
        {
          id: "High",
          value: "High",
        },
        {
          id: "Medium",
          value: "Medium",
        },
        {
          id: "Low",
          value: "Low",
        },
      ],
      duraton: [
        { id: "", value: "Choose One" },
        { id: "Day", value: "Day" },
        { id: "Week", value: "Weeks" },
        { id: "Month", value: "Month" },
        { id: "Year", value: "Year" },
      ],
      users: [],
      roleLists: [],
      taskCategories: [],
      panddingTask: [],
      customers: [],
    };
  },
  methods: {
    formName(formId){
      let formObj = {};
       for (let i = 0; i < this.forms.length; i++) {
        if (this.forms[i].id === formId) {
          formObj = this.forms[i];
        }
      }
       if(formObj){
         return formObj.value;
       }
    },
    addCategory() {
      this.categoryAddShow = true;
    },
    closeCategory() {
      this.categoryAddShow = false;
    },
    createCategory() {
      if (this.category_name == '') {
        this.message = 'Category name is required';
      } else {
        this.cat.category_name = this.category_name;
        axiosPost(TASK_CATEGORY_STORE, this.cat)
            .then((res) => {
              this.categoryAddShow = false;
              this.$toastr.s(res.data.message);
              this.getTaskCategories();
              this.category_name = '';
            })
            .catch((error) => {

            });
      }
    },
    addUser() {
      this.title = this.$t("add_user");
      this.active = 2;
    },
    backTaskCreate(value) {
      this.getUsers();
      this.active = value;
    },
    beforeSubmit() {
      this.preloader = true;
    },
    add() {
      this.date_time_data.push({
        start_date: null,
        end_date: null,
        start_time: null,
        end_time: null
      })

    },
    remove(index) {
      this.date_time_data.splice(index, 1)
    },
    submitData() {
      // console.log("inputs",this.date_time_data);
      this.date_time_data.map(val => {
        return val.start_date = this.getDateFormatForBackend(
            val.start_date
        ),
            val.end_date = this.getDateFormatForBackend(
                val.end_date
            )

      });
      this.inputs.type = "task";
      let formData = new FormData();

      if (this.checked_repeat) {
        this.inputs.checked_repeat = 1;
      }

      if (this.dropzone_files.length) {
        this.dropzone_files.forEach((dropzone_file) =>
            formData.append(`dropzone_file[]`, dropzone_file)
        );
      }

      // if (this.inputs.length > 0) {
      // Object.entries(this.inputs).forEach(value,index => formData.append(index,value));
      this.inputs.date_time_data = JSON.stringify(this.date_time_data);
      // console.log("complete ==>", this.inputs);
      Object.keys(this.inputs).forEach((key) => {
        formData.append(key, this.inputs[key]);
      });
      // }
      // let formData = new FormData();
      // formData.append(this.inputs);
      // this.inputs.dropzone_file = formData;
      this.save(formData);
      // axiosPost(TASK_STORE, formData)
      //     .then(res => {
      //         
      //         this.$toastr.s(res.data.message);
      //     })
      //     .catch (error => {
      //         
      //     });

      // this.save(this.inputs);
    },
    pendingTasks() {
      axiosGet(GET_PENDDING_TASKS)
          .then((res) => {
            this.panddingTask = res.data;
          })
          .catch((error) => {

          });
    },
    afterSuccess(res) {
      this.$toastr.s(res.data.message);
      window.location = "/admin/tasks";
    },
    afterFinalResponse() {
      this.preloader = false;
      this.inputs = {};
    },
    afterError(res) {
      this.$toastr.e(res.data.error);
    },
    clear() {
      this.inputs = {};
      this.fieldStatus = {
        isSubmit: false,
      };
    },
    getUsers() {
      axiosGet(GET_COMPANY_USERS)
          .then((res) => {
            this.users = res.data;
          })
          .catch((error) => {

          });
    },
    getTaskCategories() {
      axiosGet(GET_TASK_CATEGORIES)
          .then((res) => {
            this.taskCategories = res.data;
          })
          .catch((error) => {

          });
    },
    getCustomers() {
      axiosGet(CUSTOMERS_LIST)
          .then((response) => {

            this.customers = response.data;
          })
          .catch((error) => {

          });
    },
    getForms() {
      axiosGet(BUYER_CERTIFICATE_FORMS)
          .then((res) => {
            this.forms = res.data;
            const formRevisionId = this.$attrs.formrevisionid ?? 0

            if(formRevisionId != 0) {
              this.inputs.form_id = [formRevisionId];
            }
            // this.total_recuring_task = res.data.total_recuring_task;
          })
          .catch((error) => {

          });
    },
    refreshForm() {
      this.refreshShow = false;
      this.getForms();

    },
    createForm() {
      this.refreshShow = true;
      window.open('/admin/documentlibrary/mydocument', '_blank');
    },
    setFormIds() {
      if (this.$attrs.requestdata) {
        this.inputs.form_id = this.$attrs.requestdata.form_body_id;
      }

    },
  },
  mounted() {
    this.setFormIds();
    this.getUsers();
    this.getTaskCategories();
    // this.pendingTasks();
    this.getCustomers();
    this.getForms();
  },
};
</script>
<style scoped>
.row{
  margin-left: 0 !important;
  margin-right: 0 !important;
}
</style>